'use client';

import { gql } from '@apollo/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { Spinner } from '../Spinner';
import { InvoiceEdit, InvoiceEditProps } from '../InvoiceEdit';
import { InvoiceView } from '../InvoiceView';
import {
	EmailNotificationGroupTitle,
	InvoiceDetailInvoiceFragment,
	InvoiceStatus,
	UserType,
	useInvoiceDetail_GetInvoiceWhereQuery,
	useInvoiceDetail_GetOrgUserWhereQuery,
} from '#types';
import rpcShared from '@rockpapercoin/rpc-shared';
import { BackButton, BackButtonProps } from '../BackButton';
import { FlattenedUser } from '../../types/user';
import { useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import styles from './InvoiceDetail.module.scss';
import { useTabs } from '../../hooks/useTabs';
import ContactsContextProvider from '../../context/ContactsContextProvider/ContactsContextProvider';
import ErrorScrollContextProvider from '../../context/ErrorScrollContextProvider';
import { SegmentedControl } from '../SegmentedControl';
import { filterTypenameFromObject } from '../../lib/helpers/apollo';
import {
	deepCompareAndDiff,
	Differences,
	transformDatesToLocalTimezone,
} from '../../lib/helpers/deepCompareAndDiff';
import { useNotificationGroup } from '../NotificationGroup/useNotificationGroup';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../lib/editorBlockerIds';
import { deepCompareAndDiffOnlyWhereDefined } from '../../lib/helpers/deepCompareAndDiffOnlyWhereDefined';
import { InvoiceDetailView } from './types';

export const invoiceDetailInvoiceFragment = gql`
	fragment invoiceDetailInvoice on Invoice {
		...invoiceViewInvoice
		...invoiceEditInvoice
	}
	${ InvoiceView.fragments.invoiceViewInvoiceFragment }
	${ InvoiceEdit.fragments.invoiceEditInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getInvoiceWhere = gql`
	query invoiceDetail_getInvoiceWhere(
		$where: InvoiceWhereUniqueInput!
		$data: InvoiceTokenInput
	) {
		getInvoiceWhere(where: $where, data: $data) {
			invoice {
				contact {
					id
					vendor {
						id
					}
				}
				...invoiceDetailInvoice
			}
		}
	}
	${ invoiceDetailInvoiceFragment }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const invoiceDetailGetOrgUserWhereQuery = gql`
	query invoiceDetail_getOrgUserWhere($where: OrgUserWhereUniqueInput!) {
		getOrgUserWhere(where: $where) {
			id
			organization {
				id
				country
				automatedRemindersEnabled
				cardConvenienceFeePercentage
				achConvenienceFeePercentage
				...invoiceViewOrganization
				users {
					id
					role
					firstName
					lastName
					user {
						id
						email
						userType
					}
				}
				services {
					id
					name
				}
			}
		}
	}
	${ InvoiceView.fragments.invoiceViewOrganizationFragment }
`;

export type InvoiceDetailProps = {
	invoiceId?: string;
	guestToken?: string;
	requestingUserBaseId?: string;
	requestingUserIsGuest?: boolean;
	tab: InvoiceDetailView;
	contactId?: string;
};

export const InvoiceDetail: React.FC<InvoiceDetailProps> = ( {
	guestToken,
	invoiceId,
	requestingUserBaseId,
	requestingUserIsGuest,
	tab,
	contactId,
} ) => {
	const { showError } = useToast();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser ? user : undefined;

	const [ fileOrFilename, setFileOrFilename ] = useState<
		File | string | undefined
	>();
	const [ initialStateReady, setInitialStateReady ] = useState( false );

	const { value, handleChange, idAndAssistanceProps } = useTabs( {
		tabs: InvoiceDetailView,
		initialValue: tab,
		handleQueryParam: true,
		bypassBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
	} );

	const { data: orgUserData, error: orgUserError } =
		useInvoiceDetail_GetOrgUserWhereQuery( {
			variables: { where: { id: orgUser?.id || '' } },
			skip: !orgUser?.organization.id,
		} );

	useEffect( () => {
		if ( orgUserError ) {
			showError( orgUserError );
		}
	}, [ orgUserError, showError ] );

	const { data, error, loading } = useInvoiceDetail_GetInvoiceWhereQuery( {
		variables: {
			where: {
				id: invoiceId || '',
			},
			...( guestToken ? { data: { token: guestToken } } : {} ),
		},
		skip: !invoiceId,
	} );

	const [ invoice, setInvoice ] = useState<
		InvoiceDetailInvoiceFragment | undefined
	>();
	const [ invoiceEdits, setInvoiceEdits ] = useState<Differences | undefined>(
		undefined
	);
	const [ openCustomizeEmailModal, setOpenCustomizeEmailModal ] = useState( false );
	const [ skipInstallmentCalculation, setSkipInstallmentCalculation ] =
		useState( false );

	/* Because there can be some minor differences between what's in the backend and what
	gets calculated in the frontend, we skip the initial calculations to preserve the
	backend versions, at least until the user makes some change. */
	useEffect( () => {
		if ( loading || !invoice ) {
			setSkipInstallmentCalculation( true );
		} else {
			// Skip one render cycle to avoid the initial calculations when props change
			setTimeout( () => {
				setSkipInstallmentCalculation( false );
			}, 0 );
		}
	}, [ invoice, loading ] );

	// Because some useEffects need to know when the invoice is done loading, if there is one
	useEffect( () => {
		if ( invoiceId && invoice ) {
			setInitialStateReady( true );
		} else if ( !invoiceId ) {
			setInitialStateReady( true );
		}
	}, [ invoiceId, invoice ] );

	const organizationId =
		orgUser?.organization.id &&
		( !invoiceId || invoice?.organization?.id === orgUser.organization.id )
			? orgUser.organization.id
			: undefined;

	useEffect( () => {
		if ( error ) {
			showError( error );
		}
	}, [ error, showError ] );

	// so we can set the contract vendor with enough data to see the proper avatar
	const invoiceOrganization = useMemo(
		() => filterTypenameFromObject( orgUserData?.getOrgUserWhere?.organization ),
		[ orgUserData?.getOrgUserWhere?.organization ]
	);

	// This is the "default" user-editable data shown on /invoice/create
	const emptyInvoiceWithDefaults: Pick<
		InvoiceDetailInvoiceFragment,
		| 'title'
		| 'dueDate'
		| 'eventDate'
		| 'allowsGratuity'
		| 'automatedRemindersEnabled'
		| 'notes'
		| 'discountAmount'
	> & {
		lineItems: Pick<
			NonNullable<InvoiceDetailInvoiceFragment['lineItems']>[number],
			'name' | 'notes' | 'quantity' | 'rate' | 'taxPercentage'
		>[];
	} & {
		paymentInstallments: Pick<
			NonNullable<InvoiceDetailInvoiceFragment['paymentInstallments']>[number],
			'name' | 'amountDue' | 'dueDate'
		>[];
	} & {
		contact:
			| ( Pick<
					NonNullable<InvoiceDetailInvoiceFragment['contact']>,
					'firstName' | 'lastName'
			  > & {
					customer: Pick<
						NonNullable<InvoiceDetailInvoiceFragment['contact']>['customer'],
						'email'
					>;
			  } )
			| null;
	} = useMemo(
		() => ( {
			title: '',
			dueDate: null,
			eventDate: null,
			allowsGratuity: true,
			automatedRemindersEnabled:
				invoiceOrganization?.automatedRemindersEnabled ?? true,
			contact: { firstName: '', lastName: '', customer: { email: '' } },
			lineItems: [
				/* although `lineItems` might not have data, we load a "dummy"
				line item into `/invoice/create` at page load */
				{
					name: '',
					quantity: 1,
					rate: 0,
					taxPercentage: 0,
					notes: '',
				},
			],
			notes: '',
			paymentInstallments: [],
			emailNotificationGroup: {
				id: '',
				groupTitle: EmailNotificationGroupTitle.InvoiceCopy,
				members: [],
			},
			discountAmount: 0,
		} ),
		[ invoiceOrganization ]
	);

	const backfillRequiredInvoiceFields = useCallback(
		(
			prevState: InvoiceDetailInvoiceFragment | undefined,
			overlay?: Partial<InvoiceDetailInvoiceFragment>
		): InvoiceDetailInvoiceFragment | undefined =>
			!invoiceOrganization?.id
				? prevState ?? undefined
				: ( {
					id: prevState?.id ?? '',
					status: prevState?.status ?? InvoiceStatus.Draft,
					automatedRemindersEnabled:
							prevState?.automatedRemindersEnabled ??
							emptyInvoiceWithDefaults.automatedRemindersEnabled,
					allowsGratuity:
							prevState?.allowsGratuity ??
							emptyInvoiceWithDefaults.allowsGratuity,
					lineItems:
							prevState?.lineItems ?? emptyInvoiceWithDefaults.lineItems,
					paymentInstallments:
							prevState?.paymentInstallments ??
							emptyInvoiceWithDefaults.paymentInstallments,
					organization:
							typeof prevState?.organization === 'object'
								? prevState.organization
								: invoiceOrganization,
					...prevState,
					...( overlay ?? {} ),
				  } as InvoiceDetailInvoiceFragment ),
		[ invoiceOrganization, emptyInvoiceWithDefaults ]
	);

	const handleNotificationGroupChange = useCallback(
		(
			value: Parameters<
				Parameters<typeof useNotificationGroup>[0]['onChange']
			>[0]
		) => {
			setInvoice(
				( prevState ) =>
					backfillRequiredInvoiceFields( prevState, value ) ?? prevState
			);
		},
		[ backfillRequiredInvoiceFields ]
	);

	const useNotificationGroupProps = useNotificationGroup( {
		value: invoice?.emailNotificationGroup,
		organization: invoice?.organization,
		hideSuggestions: true,
		id: invoice?.emailNotificationGroup?.id || '',
		groupTitle: EmailNotificationGroupTitle.InvoiceCopy,
		onChange: handleNotificationGroupChange,
	} );

	const invoiceFromApi = data?.getInvoiceWhere?.invoice;
	useEffect( () => {
		if ( !loading ) {
			// setting the invoice data in state like so allows the bundle stuff in InvoiceEdit to work
			if ( invoiceFromApi ) {
				setInvoice(
					backfillRequiredInvoiceFields( invoiceFromApi ) ?? invoiceFromApi
				);
			} else {
				setInvoice( ( prevState ) =>
					// if we don't yet have a vendor/organization on the invoice, add one so that we see the proper avatar
					invoiceOrganization?.id
						? backfillRequiredInvoiceFields( prevState ) ?? prevState
						: prevState
				);
			}
		}
	}, [
		invoiceFromApi,
		loading,
		invoiceOrganization,
		backfillRequiredInvoiceFields,
	] );

	/* so we can disable features in view corresponding to values that have changed
	in edit, we diff the server-side values with what we have in local state within
	this component, and pass that into view */
	useEffect( () => {
		if ( invoice ) {
			const invoiceEdits = {
				...( data?.getInvoiceWhere?.invoice
					? {
						...deepCompareAndDiff( invoice, data.getInvoiceWhere.invoice, {
							dueDate: transformDatesToLocalTimezone,
							eventDate: transformDatesToLocalTimezone,
						} ),
						...( !useNotificationGroupProps.value.every(
							( option ) =>
								!!invoice.emailNotificationGroup?.members.find(
									( member ) => member.email === option.value
								)
						)
							? {
								emailNotificationGroup: {
									oldValue: invoice.emailNotificationGroup?.members.map(
										( member ) => member.email
									),
									newValue: useNotificationGroupProps.value.map(
										( option ) => option.value
									),
								},
								  }
							: {} ),
					  }
					: deepCompareAndDiffOnlyWhereDefined(
						invoice,
						emptyInvoiceWithDefaults,
						{
							dueDate: transformDatesToLocalTimezone,
							eventDate: transformDatesToLocalTimezone,
						}
					  ) ),
			};
			setInvoiceEdits(
				Object.keys( invoiceEdits ).length > 0 ? invoiceEdits : undefined
			);
		}
	}, [
		invoice,
		data?.getInvoiceWhere?.invoice,
		useNotificationGroupProps.value,
		emptyInvoiceWithDefaults,
	] );

	/* If the user is a guest they we don't want to show a back button. Otherwise
	we show an /invoices link unless they're an OrgUser customer, who has a tabbed
	/invoices page where we want to send them "back" to the received invoices. */
	const viewingAsOrgUserCustomer =
		user?.isLoggedIn &&
		user.userType === UserType.OrgUser &&
		invoice?.contact &&
		rpcShared.contacts.isCustomer( invoice.contact, user.user.id );

	const showTabs =
		!invoice?.organization ||
		( orgUser &&
			rpcShared.organizations.isAdminOrManager( invoice.organization, orgUser ) );

	const backButton: BackButtonProps | undefined = viewingAsOrgUserCustomer
		? {
			href: '/invoices?tab=received',
			text: 'Invoices',
		  }
		: !requestingUserIsGuest
			? {
				href: '/invoices',
				text: 'Invoices',
		  }
			: undefined;

	const showBlurb =
		( invoice && !invoice?.id && value === InvoiceDetailView.edit ) || false;

	const showBackButton = user?.isLoggedIn || false;

	return (
		<ErrorScrollContextProvider stickyHeaderOffset>
			<Box component='main' className={ styles.container }>
				{ backButton && showBackButton ? (
					<Box className={ styles.buttonRow }>
						<BackButton { ...backButton } />
					</Box>
				) : null }
				{ loading ? (
					<Box className={ styles.loading }>
						<Spinner />
					</Box>
				) : showTabs ? (
					<ContactsContextProvider
						organizationId={ organizationId }
						cacheKey={ invoiceId }
					>
						{ showBlurb ? (
							<Typography className={ styles.blurb }>
								Fill in a few fields and start building your invoice. Get
								inspired with one of our examples, or create your own design!
							</Typography>
						) : null }
						<Box className={ styles.fakePaperTop }>
							<Paper className={ styles.fakePaper }>
								<SegmentedControl
									idAndAssistanceProps={ idAndAssistanceProps }
									enumeration={ InvoiceDetailView }
									options={ {
										view: { label: 'Invoice View' },
										edit: {
											label: `Invoice ${ invoice?.id ? 'Edit' : 'Create' }`,
										},
									} }
									value={ value }
									onChange={ handleChange }
								/>
							</Paper>
						</Box>
						<Box
							role='tabpanel'
							hidden={ value !== InvoiceDetailView.view }
							id={ `invoice-view-${ invoiceId }` }
							aria-labelledby={ idAndAssistanceProps( InvoiceDetailView.view ).id }
							className={ styles.tabPanel }
						>
							<InvoiceView
								tabbedView={ true }
								invoice={ invoice }
								guestToken={ guestToken }
								requestingUserBaseId={ requestingUserBaseId }
								requestingUserIsGuest={ requestingUserIsGuest }
								fileOrFilename={ fileOrFilename }
								invoiceEdits={ invoiceEdits }
							/>
						</Box>
						<Box
							role='tabpanel'
							hidden={ value !== InvoiceDetailView.edit }
							id={ `invoice-edit-${ invoiceId }` }
							aria-labelledby={ idAndAssistanceProps( InvoiceDetailView.edit ).id }
							className={ styles.tabPanel }
						>
							<InvoiceEdit
								isVisible={ value === InvoiceDetailView.edit }
								invoiceId={ invoiceId }
								invoice={ invoice }
								contactId={ contactId }
								fileOrFilename={ fileOrFilename }
								setFileOrFilename={ setFileOrFilename }
								/* this "as" does assertion, not casting. You can test that by changing the useState for invoice to:
								```
								const [ invoice, setInvoice ] = useState<
								Omit<InvoiceDetailInvoiceFragment, 'status'> | undefined
								>();
								```
								and see the linter error because InvoiceEditProps['setInvoice'] requires status
								*/
								setInvoice={ setInvoice as InvoiceEditProps['setInvoice'] }
								notificationGroupProps={ useNotificationGroupProps }
								openCustomizeEmailModal={ openCustomizeEmailModal }
								setOpenCustomizeEmailModal={ setOpenCustomizeEmailModal }
								skipInstallmentCalculation={ skipInstallmentCalculation }
								setSkipInstallmentCalculation={ setSkipInstallmentCalculation }
								invoiceEdits={ invoiceEdits }
								initialStateReady={ initialStateReady }
							/>
						</Box>
					</ContactsContextProvider>
				) : (
					<InvoiceView
						tabbedView={ false }
						invoice={ invoice }
						guestToken={ guestToken }
						requestingUserBaseId={ requestingUserBaseId }
						requestingUserIsGuest={ requestingUserIsGuest }
					/>
				) }
			</Box>
		</ErrorScrollContextProvider>
	);
};
